import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { IconButton } from './ui/IconButton.js';

const SIDEBAR_W = 200;
const SIDEBAR_W_COLLAPSED = 80;

interface DashboardLastRun {
  email: { lastRun: { startedAt: string } | null };
}

function ago(iso: string): string {
  const ms = Date.now() - new Date(iso.endsWith('Z') ? iso : iso + 'Z').getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

interface AppFooterProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function AppFooter({ collapsed, onToggle }: AppFooterProps) {
  const { t } = useTranslation();
  const sidebarW = collapsed ? SIDEBAR_W_COLLAPSED : SIDEBAR_W;

  const { data } = useQuery<DashboardLastRun>({
    queryKey: ['dashboard-summary'],
    queryFn: () => api.get<DashboardLastRun>('/api/dashboard/summary'),
    staleTime: 60_000,
  });

  const lastSync = data?.email.lastRun
    ? t('home.footer.last_sync', { time: ago(data.email.lastRun.startedAt) })
    : null;

  return (
    <footer
      style={{
        display: 'grid',
        gridTemplateColumns: `${sidebarW}px 1fr`,
        height: '44px',
        flexShrink: 0,
        borderTop: '1px solid var(--evo-stroke)',
        transition: 'grid-template-columns 180ms ease',
      }}
    >
      {/* Sidebar zone — version + toggle */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          padding: collapsed ? '0 8px' : '0 8px 0 24px',
          borderRight: '1px solid var(--evo-stroke)',
          transition: 'padding 180ms ease',
        }}
      >
        {!collapsed && (
          <span style={{ color: 'var(--evo-fg-4)', fontSize: '11px' }}>v0.1.0</span>
        )}
        <IconButton
          variant="ghost"
          tone="muted"
          size="sm"
          ariaLabel={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          icon={
            <svg
              width="16" height="16" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="1.5"
              strokeLinecap="round" strokeLinejoin="round"
            >
              {collapsed
                ? <path d="M9 18l6-6-6-6" />
                : <path d="M15 18l-6-6 6-6" />}
            </svg>
          }
          onClick={onToggle}
        />
      </div>

      {/* Content zone — last sync right-aligned */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          padding: '0 var(--evo-content-pad-x)',
        }}
      >
        {lastSync && (
          <span style={{ fontSize: '11px', color: 'var(--evo-fg-4)' }}>
            {lastSync}
          </span>
        )}
      </div>
    </footer>
  );
}
